/**
 * QuestionWrapper Component
 * 
 * Base wrapper for all question types.
 * Provides:
 * - Consistent layout and styling
 * - Animation between questions
 * - Navigation buttons
 * - Error display
 * 
 * All specific question components use this wrapper.
 */

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardHeader, CardFooter } from '../UI/Card';
import { Button } from '../UI/Button';
import { Checkbox } from '../UI/Checkbox';
import { Logo } from '../UI/Logo';
import { Question } from '../../types/funnel.types';
import { trackStepViewed } from '../../utils/analytics';

// ============================================
// TYPES
// ============================================

interface QuestionWrapperProps {
  /** Question data */
  question: Question;
  /** Current step number */
  currentStep: number;
  /** Total steps for display */
  totalSteps: number;
  /** Whether this is the first step */
  isFirst: boolean;
  /** Whether this is the last question step */
  isLast: boolean;
  /** Can proceed to next step */
  canProceed: boolean;
  /** Is form submitting/loading */
  isLoading?: boolean;
  /** Error message to display */
  error?: string;
  /** Next button click handler */
  onNext: () => void;
  /** Back button click handler */
  onBack: () => void;
  /** Question content */
  children: React.ReactNode;
  /** Whether this is question 1 (for special styling) */
  isQuestion1?: boolean;
  /** Terms agreement checkbox state (question 1 only) */
  termsAgreed?: boolean;
  /** Terms agreement change handler (question 1 only) */
  onTermsChange?: (checked: boolean) => void;
  /** Terms checkbox error state (question 1 only) */
  termsError?: boolean;
}

// ============================================
// ANIMATION VARIANTS
// ============================================

/**
 * Page transition animations
 */
const pageVariants = {
  initial: {
    opacity: 0,
    x: 50,
    scale: 0.98,
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    x: -50,
    scale: 0.98,
    transition: {
      duration: 0.3,
    },
  },
};

// ============================================
// COMPONENT
// ============================================

/**
 * QuestionWrapper - Container for question content with navigation
 */
export const QuestionWrapper: React.FC<QuestionWrapperProps> = ({
  question,
  currentStep,
  totalSteps,
  isFirst,
  isLast,
  canProceed,
  isLoading = false,
  error,
  onNext,
  onBack,
  children,
  isQuestion1 = false,
  termsAgreed = false,
  onTermsChange,
  termsError = false,
}) => {
  // Track step view on mount
  useEffect(() => {
    trackStepViewed(currentStep, question.id);
  }, [currentStep, question.id]);

  // Determine button text
  const nextButtonText = isLast ? 'Submit' : 'Continue';
  const nextButtonIcon = isLast ? (
    <CheckIcon />
  ) : (
    <ArrowRightIcon />
  );

  // Enhanced card styling with glassmorphic effect for all questions
  const cardClassName = 'bg-white/95 backdrop-blur-sm shadow-2xl ring-4 ring-white/30';

  const headerClassName = '';

  return (
    <motion.div
      key={question.id}
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="w-full max-w-question mx-auto px-4 sm:px-6"
    >
      {/* Logo at top - overlapping the card */}
      <div className="relative z-20 -mb-20 w-full">
        <Logo size="md" animate={true} />
      </div>

      <Card className={`${cardClassName} pt-24`}>
        {/* Question Header */}
        <CardHeader
          step={`Question ${currentStep} of ${totalSteps - 1}`}
          title={question.question}
          subtitle={question.subtext}
          className={headerClassName}
        />

        {/* Question Content */}
        <div className="min-h-[200px]">
          {children}
        </div>

        {/* Error Display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg"
            >
              <p className="text-sm text-red-600 flex items-center gap-2">
                <ErrorIcon />
                {error}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Terms Agreement Checkbox (Question 1 only) */}
        {isQuestion1 && onTermsChange && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6"
          >
            <Checkbox
              checked={termsAgreed}
              onChange={(e) => onTermsChange(e.target.checked)}
              error={termsError}
              errorMessage={termsError ? 'You must agree to the terms to continue' : undefined}
              label={
                <span className="text-sm text-text-secondary">
                  By continuing, you agree to our{' '}
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    Terms of Service
                  </a>
                  {' '}and acknowledge our{' '}
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    Privacy Policy
                  </a>
                  {' '}and{' '}
                  <a 
                    href="/cookies" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    Cookie Policy
                  </a>
                  .
                </span>
              }
            />
          </motion.div>
        )}

        {/* Navigation Buttons */}
        <CardFooter className="flex-col-reverse sm:flex-row">
          {/* Back button */}
          {!isFirst && (
            <Button
              variant="secondary"
              onClick={onBack}
              disabled={isLoading}
              leftIcon={<ArrowLeftIcon />}
              className="w-full sm:w-auto"
            >
              Back
            </Button>
          )}

          {/* Spacer for first step */}
          {isFirst && <div className="hidden sm:block" />}

          {/* Next/Submit button */}
          <Button
            variant="primary"
            onClick={onNext}
            disabled={!canProceed || isLoading}
            isLoading={isLoading}
            rightIcon={!isLoading ? nextButtonIcon : undefined}
            fullWidth
            className="sm:w-auto sm:min-w-[160px]"
          >
            {nextButtonText}
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

QuestionWrapper.displayName = 'QuestionWrapper';

// ============================================
// ICON COMPONENTS
// ============================================

const ArrowRightIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const ArrowLeftIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ErrorIcon: React.FC = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

export default QuestionWrapper;
